// The outbox: orders and messages made with no signal wait here, on the phone,
// and send once there is signal. Each carries a one-time key (its id), so a
// send that repeats after a dropped connection is answered, not repeated.
// public/sw.js reads the same database to send in the background on Android.

const DB = 'buds-offline';
const STORE = 'outbox';

function open() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB, 1);
    request.onupgradeneeded = () =>
      request.result.createObjectStore(STORE, { keyPath: 'id' });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function run(mode, work) {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const result = work(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(result?.result ?? result);
    tx.onerror = () => reject(tx.error);
  });
}

export const all = () => run('readonly', (s) => s.getAll());
export const put = (entry) => run('readwrite', (s) => s.put(entry));
export const remove = (id) => run('readwrite', (s) => s.delete(id));

export function newId() {
  return crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function changed() {
  window.dispatchEvent(new CustomEvent('outbox:changed'));
}

// Keeps an order or message on the phone until it sends.
export async function keep(entry, { background = true } = {}) {
  await put({
    status: 'waiting',
    createdAt: new Date().toISOString(),
    ...entry,
  });
  changed();
  if (background) await registerBackground();
}

// Lets Android send in the background once there is signal.
export async function registerBackground() {
  try {
    const registration = await navigator.serviceWorker?.ready;
    await registration?.sync?.register('buds-outbox');
  } catch {
    // No background sync (iPhone): it sends when the app is open with signal.
  }
}

function csrf() {
  return document.querySelector('meta[name="csrf-token"]')?.content;
}

async function freshCsrf() {
  const response = await fetch('/session/token', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return response.ok ? (await response.json()).token : null;
}

function body(entry) {
  const form = new FormData();
  Object.entries(entry.fields || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) value.forEach((v) => form.append(key, v));
    else if (value !== null && value !== undefined) form.append(key, value);
  });
  (entry.photos || []).forEach((photo) =>
    form.append('photos[]', photo.blob, photo.name),
  );
  return form;
}

// One try. Returns 'sent', 'refused', or 'offline' (still waiting).
export async function send(
  entry,
  token = csrf(),
  retried = false,
  waited = false,
) {
  let response;
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    response = await fetch(entry.url, {
      method: 'POST',
      body: body(entry),
      credentials: 'same-origin',
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        'Idempotency-Key': entry.id,
        'X-CSRF-Token': token || '',
      },
    });
    clearTimeout(timer);
  } catch {
    return { state: 'offline' };
  }

  // The page's token can go stale while an order waits; get a fresh one once.
  if (
    response.status === 422 &&
    !retried &&
    !(response.headers.get('content-type') || '').includes('json')
  ) {
    const fresh = await freshCsrf().catch(() => null);
    if (fresh) return send(entry, fresh, true, waited);
  }
  if (response.status === 401 || response.redirected)
    return { state: 'refused', message: 'Sign in again to send this.' };

  let data = {};
  try {
    data = await response.json();
  } catch {
    // Not JSON: treat as not yet sent.
  }
  if (response.ok) {
    await remove(entry.id);
    changed();
    return { state: 'sent', data };
  }
  // 409: the same order is mid-send from elsewhere; its answer comes shortly.
  if (response.status === 409 && !waited) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    return send(entry, token, retried, true);
  }
  if (response.status === 409 || response.status >= 500)
    return { state: 'offline' };

  await put({
    ...entry,
    status: 'refused',
    message: data.message || "Bud's could not take this. Call 270-765-4452.",
  });
  changed();
  return { state: 'refused', data };
}

let flushing = false;

// Sends everything waiting, oldest first; stops at the first sign of no signal.
export async function flush() {
  if (flushing) return;
  flushing = true;
  try {
    const waiting = (await all())
      .filter((e) => e.status === 'waiting')
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    for (const entry of waiting) {
      const result = await send(entry);
      window.dispatchEvent(
        new CustomEvent('outbox:result', { detail: { entry, ...result } }),
      );
      if (result.state === 'offline') break;
    }
  } finally {
    flushing = false;
  }
}
